import { getCurrentUsername, MetaTags, MetaTagsService } from '@mode/shared/util-js';
import { ModalService } from '../modal.service';
import { ModeResourceHelper } from '@mode/shared/util-halogen';

type OpenAttributes = {
  report: any;
  run: any;
  reportOwner: any;
  account: any;
  view: any;
  schedule: any;
  onSchedulesUpdate: () => void;
};

type OpenFromShareModalAttributes = {
  report: any;
  run: any;
  reportOwner: any;
  account: any;
};

const template = `
  <div class="save-modal schedules-modal default-modal ds-modal">
    <mode-new-schedules
      report="ctrl.report"
      run="ctrl.run"
      start-schedule="ctrl.schedule"
      dismiss="ctrl.$dismiss()"
      account="ctrl.account"
      owner="ctrl.reportOwner"
      view="{{ ctrl.view }}"
      on-update="ctrl.onSchedulesUpdate()">
    </mode-new-schedules>
  </div>
`;

export class SchedulesModalService {
  static $inject = [
    'ModalService',
    'MetaTagsService',
    'AccountModel',
    'ReportModel',
    'ReportRunModel',
    'ReportScheduleModel',
  ];

  constructor(
    private modalService: ModalService,
    private metaTags: MetaTagsService,
    private AccountModel: any,
    private ReportModel: any,
    private ReportRunModel: any,
    private ReportScheduleModel: any
  ) {}

  open(attributes: OpenAttributes) {
    this.modalService.open(attributes, {
      bindToController: true,
      controller: function () {},
      controllerAs: 'ctrl',
      template,
    });
  }

  openFromShareModal(attributes: OpenFromShareModalAttributes) {
    this.modalService.open(attributes, {
      controller: function () {},
      controllerAs: 'ctrl',
      bindToController: true,
      template,
    });
  }

  async getSchedulesResources(reportToken: string, orgUsername?: string) {
    const currentOrgUsername = (orgUsername || this.metaTags.getTag(MetaTags.OwnerUsername)) ?? '';
    const [account, { owner, report, reportRun, schedules }] = await Promise.all([
      this.currentUser(this.AccountModel),
      this.fetchSchedulesResources(reportToken, currentOrgUsername),
    ]);

    return {
      account,
      owner,
      report,
      reportRun,
      schedules,
    };
  }

  async fetchSchedulesResources(reportToken: string, orgUsername: string) {
    const [owner, report] = await Promise.all([
      this.currentOrganization(null, this.AccountModel, orgUsername),
      this.ReportModel.find({
        token: reportToken,
        account: orgUsername,
        useEntryPath: true,
        includes: ['new_report_schedule', 'new_report_subscription'],
      }),
    ]);

    const [reportRun, schedules] = await Promise.all([
      report
        .reportRuns({
          includes: {
            report_runs: ['new_report_run_email'],
          },
        })
        .then((reportRunsCollection: any) =>
          ModeResourceHelper.resourceToModel(
            reportRunsCollection._resource.embedded('report_runs')[0],
            this.ReportRunModel.model
          )
        ),
      report
        .reportSchedules({
          includes: {
            report_schedules: {
              report_subscriptions: {
                report_subscriptions: [
                  'new_email_subscription_membership',
                  'slack_subscription_memberships',
                  'new_slack_subscription_membership',
                ],
              },
            },
          },
          refresh: true,
        })
        .then((schedulesCollection: any) =>
          ModeResourceHelper.resourcesToModels(
            schedulesCollection._resource.embedded('report_schedules'),
            this.ReportScheduleModel.model
          )
        ),
    ]);
    return {
      owner,
      report,
      reportRun,
      schedules,
    };
  }

  currentUser(accountModelClass: any): Promise<{ _resource: any }> {
    return accountModelClass.find({
      username: this.getCurrentUsername(),
      useEntryPath: true,
    });
  }

  private getCurrentUsername(): string | null {
    return getCurrentUsername(window.document);
  }

  private currentOrganization(config: any, accountModelClass: any, orgUsername: string): Promise<{ _resource: any }> {
    return accountModelClass.find({
      username: orgUsername,
      useEntryPath: true,
      ...config,
    });
  }
}
